/**
 * @module UploadReceiptModalStagedView
 * @purpose Presentational sub-components for the staged-transport steering
 *   flow (spec FR-staged): probing spinner, staging instructions with
 *   copy-to-clipboard, the register action, and the inline verify progress
 *   (via the shared OperationProgress facility).
 * @depends components/common (OperationProgress), upload-receipt-modal.staged
 * @dependedOnBy UploadReceiptModal
 *
 * @rules
 * - State: all state passed via props from the parent -- purely presentational
 * - Errors: role="alert" on error banners
 */

import { useState, type ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Copy } from 'lucide-react'
import { OperationProgress } from '../../common'
import type { OperationProgress as OperationProgressData, StagingInstructions } from '../../../types/evidence-containers'
import type { UploadReceiptModalState } from './use-upload-receipt-modal'

export function ProbingState({ t }: { t: TFunction<'cases'> }): ReactElement {
  return (
    <p className="py-2 text-sm text-slate-500 dark:text-slate-400" role="status" aria-busy="true">
      {t('operationProgress.checking')}
    </p>
  )
}

interface ProbeErrorStateProps {
  message: string
  onRetry: () => void
  t: TFunction<'cases'>
}

export function ProbeErrorState({ message, onRetry, t }: ProbeErrorStateProps): ReactElement {
  return (
    <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/50">
      <p className="text-sm text-red-700 dark:text-red-300">{t('detail.collections.uploadReceipt.probeError')}</p>
      <p className="mt-1 text-xs text-red-600 dark:text-red-400">{message}</p>
      <button type="button" onClick={onRetry} className="mt-2 text-xs font-medium text-red-700 hover:underline dark:text-red-300">
        {t('operationProgress.retry')}
      </button>
    </div>
  )
}

interface StagingInstructionsCardProps {
  instructions: StagingInstructions
  registering: boolean
  onRegister: () => void
  t: TFunction<'cases'>
}

const CLIPBOARD_MESSAGE_MS = 2000

export function StagingInstructionsCard({
  instructions,
  registering,
  onRegister,
  t,
}: StagingInstructionsCardProps): ReactElement {
  const [copied, setCopied] = useState(false)

  const handleCopy = (): void => {
    void navigator.clipboard.writeText(instructions.directory).then(() => {
      setCopied(true)
      globalThis.setTimeout(() => { setCopied(false) }, CLIPBOARD_MESSAGE_MS)
    })
  }

  return (
    <div className="space-y-2 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('detail.collections.uploadReceipt.stagingTitle')}
      </p>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        {t('detail.collections.uploadReceipt.stagingSubtitle')}
      </p>
      <div className="flex items-center gap-2">
        <code className="flex-1 truncate rounded bg-slate-100 px-2 py-1 text-xs dark:bg-slate-900">
          {instructions.directory}
        </code>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs text-slate-600 dark:border-slate-600 dark:text-slate-300"
        >
          <Copy className="h-3 w-3" />
          {copied ? t('detail.collections.uploadReceipt.stagingCopied') : t('detail.collections.uploadReceipt.stagingCopyDirectory')}
        </button>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        {t('detail.collections.uploadReceipt.stagingExpectedFileName')}: {instructions.expectedFileName}
      </p>
      <button
        type="button"
        onClick={onRegister}
        disabled={registering}
        className="w-full rounded-lg px-4 py-2 text-xs font-medium text-white disabled:opacity-50"
        style={{ background: 'var(--accent, #4e5e82)' }}
      >
        {registering
          ? t('detail.collections.uploadReceipt.stagingRegistering')
          : t('detail.collections.uploadReceipt.stagingRegister')}
      </button>
    </div>
  )
}

interface StagedVerifyProgressProps {
  caseId: string
  operationId: string
  onComplete: (progress: OperationProgressData) => void
}

export function StagedVerifyProgress({ caseId, operationId, onComplete }: StagedVerifyProgressProps): ReactElement {
  return <OperationProgress caseId={caseId} operationId={operationId} onComplete={onComplete} />
}

interface StagedRegisterErrorProps {
  message: string
  onRetry: () => void
  t: TFunction<'cases'>
}

function StagedRegisterError({ message, onRetry, t }: StagedRegisterErrorProps): ReactElement {
  return (
    <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/50">
      <p className="text-sm text-red-700 dark:text-red-300">{message}</p>
      <button type="button" onClick={onRetry} className="mt-2 text-xs font-medium text-red-700 hover:underline dark:text-red-300">
        {t('containerTriage.retry')}
      </button>
    </div>
  )
}

interface StagedVerifyFailedProps {
  cause: string
  onRetry: () => void
  t: TFunction<'cases'>
}

/**
 * @behaviour Shown once a staged verification pass terminates as `failed`
 *   and the analyst acknowledges the completion dialogue: names the
 *   failure cause read out of `outcomeSummary` and offers a "Retry
 *   verification" action that re-triggers the pass against the SAME
 *   receipt -- the analyst fixes the file on disc, no new collection
 *   request needed.
 */
function StagedVerifyFailed({ cause, onRetry, t }: StagedVerifyFailedProps): ReactElement {
  return (
    <div role="alert" className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/50">
      <p className="text-sm text-red-700 dark:text-red-300">
        {t('detail.collections.uploadReceipt.stagingVerifyFailed')}
      </p>
      <p className="text-xs text-red-600 dark:text-red-400">{cause}</p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-lg px-4 py-2 text-xs font-medium text-white"
        style={{ background: 'var(--accent, #4e5e82)' }}
      >
        {t('detail.collections.uploadReceipt.stagingReverify')}
      </button>
    </div>
  )
}

export interface StagedTransportActionsProps {
  state: UploadReceiptModalState
  caseId: string
  t: TFunction<'cases'>
}

/**
 * @behaviour Picks the right sub-view for the staged sub-flow: register card
 *   (idle/registering), inline verify progress (verifying), a retryable
 *   verification-failure banner (verifyFailed) -- retry re-triggers
 *   verification on the SAME receipt via `retryStagedVerification` -- or a
 *   retryable registration error banner (error), whose retry starts
 *   registration over.
 */
export function StagedTransportActions({ state, caseId, t }: StagedTransportActionsProps): ReactElement {
  if (state.stagedStep === 'verifying' && state.stagedOperationId) {
    return (
      <StagedVerifyProgress
        caseId={caseId}
        operationId={state.stagedOperationId}
        onComplete={(progress) => { void state.handleStagedVerifyComplete(progress) }}
      />
    )
  }
  if (state.stagedStep === 'verifyFailed' && state.stagedError) {
    return (
      <StagedVerifyFailed
        cause={state.stagedError}
        onRetry={() => { void state.retryStagedVerification() }}
        t={t}
      />
    )
  }
  if (state.stagedStep === 'error' && state.stagedError) {
    return <StagedRegisterError message={state.stagedError} onRetry={state.resetStaged} t={t} />
  }
  if (state.transportProbe?.stagingInstructions) {
    return (
      <StagingInstructionsCard
        instructions={state.transportProbe.stagingInstructions}
        registering={state.stagedStep === 'registering'}
        onRegister={() => { void state.handleRegisterStaged() }}
        t={t}
      />
    )
  }
  return <></>
}
